import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Car, MapPin, Cog, Sparkles, Camera, FileLock, Eye, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import MediaUploader from "@/components/MediaUploader";
import SpecializedListingFields from "@/components/SpecializedListingFields";
import { AUTO_CATEGORIES } from "@/lib/catalog";

const CATEGORIES = Object.entries(AUTO_CATEGORIES).map(([value, label]) => ({ value, label }));

const OPTIONS_BASE = [
  "Climatisation", "GPS", "Régulateur de vitesse", "Caméra de recul",
  "Radar de recul", "Toit ouvrant", "Sièges cuir", "Sièges chauffants",
  "Bluetooth", "CarPlay / Android Auto", "Jantes alliage", "Vitres électriques",
  "Verrouillage centralisé", "Airbags", "ABS", "ESP", "Attelage",
  "Feux LED", "Démarrage sans clé", "Aide au stationnement",
];

const STATUSES = [
  { value: "available", label: "Disponible" },
  { value: "negotiation", label: "En négociation" },
  { value: "pending", label: "Réservé" },
  { value: "sold", label: "Vendu" },
  { value: "rented", label: "Loué" },
  { value: "inactive", label: "Désactivé" },
];

const empty = {
  sector: "auto",
  purpose: "sale",
  category: "voiture",
  title: "",
  title_en: "",
  description: "",
  description_en: "",
  price: "",
  currency: "USD",
  price_period: "",
  negotiable: false,
  country: "RDC",
  city: "Kinshasa",
  zone: "",
  address: "",
  specs: {
    marque: "",
    modele: "",
    annee: "",
    vin: "",
    immatriculation: "",
    km: "",
    carburant: "",
    transmission: "",
    motricite: "",
    cylindree: "",
    puissance_ch: "",
    portes: "",
    places: "",
    couleur_ext: "",
    couleur_int: "",
    etat: "",
    proprietaires: "",
    origine: "",
    dedouanee: false,
    charge_utile: "",
    heures_travail: "",
    service_location: "sans_chauffeur",
    caution_location: "",
    kilometrage_inclus: "",
    frais_depassement: "",
    zone_circulation: "",
    assurance: "",
    livraison_vehicule: false,
    conditions_location: "",
  },
  equipment: [],
  images: [],
  videos: [],
  documents: [],
  private_documents: [],
  private_data: {},
  publication_stage: "published",
  status: "available",
  featured: false,
};

const STEPS = [
  { key: "type", label: "Type & objet", icon: Car },
  { key: "identity", label: "Identité véhicule", icon: Cog },
  { key: "specs", label: "Caractéristiques", icon: Cog },
  { key: "options", label: "Options", icon: Sparkles },
  { key: "location", label: "Localisation", icon: MapPin },
  { key: "description", label: "Description & prix", icon: FileLock },
  { key: "media", label: "Médias", icon: Camera },
  { key: "documents", label: "Documents privés", icon: FileLock },
  { key: "publish", label: "Publication", icon: Eye },
];

export default function WizardAuto() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = Boolean(id);
  const [searchParams] = useSearchParams();
  const initialForm = { ...empty, purpose: searchParams.get("purpose") || empty.purpose, category: searchParams.get("category") || empty.category };
  const [step, setStep] = useState(editing ? 0 : (searchParams.has("purpose") || searchParams.has("category") ? 1 : 0));
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!editing) return;
    setLoading(true);
    setLoadError("");
    api
      .get(`/admin/listings/${id}`)
      .then((r) => {
        setForm({ ...empty, ...r.data, specs: { ...empty.specs, ...(r.data.specs || {}) } });
        setStep(5);
      })
      .catch((e) => {
        const message = formatApiError(e.response?.data?.detail) || "Impossible de charger cette annonce.";
        setLoadError(message);
        toast.error(message);
      })
      .finally(() => setLoading(false));
  }, [editing, id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateSpec = (k, v) => setForm((f) => ({ ...f, specs: { ...f.specs, [k]: v } }));
  const toggleOpt = (name) =>
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(name) ? f.equipment.filter((x) => x !== name) : [...f.equipment, name],
    }));

  const isRent = form.purpose === "rent";
  const isHeavy = ["camion", "engin"].includes(form.category);
  const isMoto = form.category === "moto";

  const canProceed = useMemo(() => {
    if (step === 0) return form.category && form.purpose;
    if (step === 1) return form.specs.marque && form.specs.modele && form.specs.annee;
    if (step === 4) return form.city;
    if (step === 5) return form.title.trim() && Number(form.price) > 0;
    return true;
  }, [step, form]);

  const submit = async () => {
    setSaving(true);
    try {
      if (!form.title.trim()) {
        // auto-title from spec if empty
        const t = [form.specs.marque, form.specs.modele, form.specs.annee].filter(Boolean).join(" ");
        if (t) form.title = t;
      }
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        specs: Object.fromEntries(
          Object.entries(form.specs).map(([k, v]) => [k, typeof v === "string" && v !== "" && !isNaN(v) ? Number(v) : v])
        ),
      };
      if (editing) await api.put(`/listings/${id}`, payload);
      else await api.post("/listings", payload);
      toast.success(editing ? "Annonce mise à jour" : "Annonce créée");
      nav("/admin/listings");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (editing && loadError) return <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700"><div className="font-semibold">Annonce non chargée</div><p className="mt-1 text-sm">{loadError}</p><Link to="/admin/listings" className="mt-4 inline-flex text-sm font-semibold underline">Retour aux annonces</Link></div>;

  return (
    <div className="space-y-6" data-testid="wizard-auto">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-orange font-semibold">{editing ? "Édition de l'annonce" : "Wizard Automobile"}</div>
          <h1 className="mt-1 font-display text-3xl lg:text-4xl text-brand-navy tracking-tight">
            {editing ? "Modifier une annonce" : "Nouvelle annonce automobile"}
          </h1>
        </div>
        <Link to="/admin/listings" className="text-sm text-slate-500 hover:text-brand-navy inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Retour à l&apos;inventaire
        </Link>
      </div>

      {editing && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div><div className="text-sm font-bold text-brand-navy">Vous modifiez : {form.title}</div><div className="text-xs text-slate-600">Changez le prix ou n'importe quel champ, puis enregistrez.</div></div>
          <Button type="button" onClick={submit} disabled={saving || !form.title.trim() || Number(form.price) <= 0} className="bg-brand-orange hover:bg-brand-orangeDark text-white" data-testid="edit-save-top"><Check className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les modifications"}</Button>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
        <ol className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => (editing || done || active ? setStep(i) : null)}
                  className={`w-full flex items-center gap-2 rounded-lg px-2.5 py-2 border text-left transition-colors ${
                    active
                      ? "border-brand-orange bg-brand-orange/10 text-brand-navy"
                      : done
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-white text-slate-500"
                  }`}
                  data-testid={`step-${s.key}`}
                >
                  <span
                    className={`h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      active ? "bg-brand-orange text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </span>
                  <span className="hidden md:inline text-[11px] font-semibold truncate">{s.label}</span>
                  <Icon className="h-3.5 w-3.5 md:hidden" />
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:p-8 shadow-sm space-y-6">
        {step === 0 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Que voulez-vous faire ?</h2>
            <div className="grid grid-cols-2 gap-3">
              {["sale", "rent"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => update("purpose", p)}
                  className={`rounded-xl border-2 p-4 text-left transition-colors ${
                    form.purpose === p ? "border-brand-orange bg-brand-orange/5" : "border-slate-200 hover:border-brand-orange/60"
                  }`}
                  data-testid={`purpose-${p}`}
                >
                  <div className="text-lg font-bold text-brand-navy">{p === "sale" ? "Vendre" : "Louer"}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    {p === "sale" ? "Mise en vente avec transfert de propriété." : "Location courte ou longue durée avec caution."}
                  </div>
                </button>
              ))}
            </div>

            <div>
              <Label className="text-sm font-semibold text-brand-navy">Type de véhicule</Label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => update("category", c.value)}
                    className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                      form.category === c.value
                        ? "border-brand-orange bg-brand-orange text-white font-semibold"
                        : "border-slate-200 hover:border-brand-orange/60"
                    }`}
                    data-testid={`category-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Identité du véhicule</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Marque *" v={form.specs.marque} onChange={(v) => updateSpec("marque", v)} tid="field-marque" />
              <Field label="Modèle *" v={form.specs.modele} onChange={(v) => updateSpec("modele", v)} tid="field-modele" />
              <Field label="Année *" type="number" v={form.specs.annee} onChange={(v) => updateSpec("annee", v)} tid="field-annee" />
              <Field label="VIN / N° châssis" v={form.specs.vin} onChange={(v) => updateSpec("vin", v)} tid="field-vin" />
              <Field label="Plaque d'immatriculation" v={form.specs.immatriculation} onChange={(v) => updateSpec("immatriculation", v)} />
              <div>
                <Label>Origine</Label>
                <Select value={form.specs.origine || ""} onValueChange={(v) => updateSpec("origine", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="importee_ue">Importée Europe</SelectItem>
                    <SelectItem value="importee_us">Importée USA</SelectItem>
                    <SelectItem value="importee_asie">Importée Asie</SelectItem>
                    <SelectItem value="importee_afrique_sud">Importée Afrique du Sud</SelectItem>
                    <SelectItem value="locale">Locale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="col-span-2 md:col-span-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.specs.dedouanee}
                  onChange={(e) => updateSpec("dedouanee", e.target.checked)}
                  data-testid="field-dedouanee"
                />
                <span>Dédouanée / documents en règle</span>
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Caractéristiques techniques</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Field label="Kilométrage" type="number" v={form.specs.km} onChange={(v) => updateSpec("km", v)} tid="field-km" />
              <div>
                <Label>Carburant</Label>
                <Select value={form.specs.carburant || ""} onValueChange={(v) => updateSpec("carburant", v)}>
                  <SelectTrigger data-testid="field-carburant"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="essence">Essence</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="hybride">Hybride</SelectItem>
                    <SelectItem value="electrique">Électrique</SelectItem>
                    <SelectItem value="gpl">GPL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Transmission</Label>
                <Select value={form.specs.transmission || ""} onValueChange={(v) => updateSpec("transmission", v)}>
                  <SelectTrigger data-testid="field-transmission"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manuelle">Manuelle</SelectItem>
                    <SelectItem value="automatique">Automatique</SelectItem>
                    <SelectItem value="semi_auto">Semi-automatique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Motricité</Label><Select value={form.specs.motricite || ""} onValueChange={(v) => updateSpec("motricite", v)}><SelectTrigger><SelectValue placeholder="2WD, 4WD ou AWD" /></SelectTrigger><SelectContent><SelectItem value="2wd">2 roues motrices</SelectItem><SelectItem value="4wd">4x4</SelectItem><SelectItem value="awd">Transmission intégrale</SelectItem></SelectContent></Select></div>
              <Field label="Cylindrée (cc)" type="number" v={form.specs.cylindree} onChange={(v) => updateSpec("cylindree", v)} />
              <Field label="Puissance (ch)" type="number" v={form.specs.puissance_ch} onChange={(v) => updateSpec("puissance_ch", v)} />
              {!isMoto && (
                <>
                  <Field label="Portes" type="number" v={form.specs.portes} onChange={(v) => updateSpec("portes", v)} />
                  <Field label="Places" type="number" v={form.specs.places} onChange={(v) => updateSpec("places", v)} />
                </>
              )}
              <Field label="Couleur extérieure" v={form.specs.couleur_ext} onChange={(v) => updateSpec("couleur_ext", v)} />
              <Field label="Couleur intérieure" v={form.specs.couleur_int} onChange={(v) => updateSpec("couleur_int", v)} />
              <div>
                <Label>État</Label>
                <Select value={form.specs.etat || ""} onValueChange={(v) => updateSpec("etat", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neuf">Neuf</SelectItem>
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="tres_bon">Très bon</SelectItem>
                    <SelectItem value="bon">Bon</SelectItem>
                    <SelectItem value="a_reviser">À réviser</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Nombre de propriétaires" type="number" v={form.specs.proprietaires} onChange={(v) => updateSpec("proprietaires", v)} />
              {isHeavy && (
                <>
                  <Field label="Charge utile (tonnes)" type="number" v={form.specs.charge_utile} onChange={(v) => updateSpec("charge_utile", v)} />
                  <Field label="Heures de travail" type="number" v={form.specs.heures_travail} onChange={(v) => updateSpec("heures_travail", v)} />
                </>
              )}
              {isRent && <><div><Label>Service de location</Label><Select value={form.specs.service_location || "sans_chauffeur"} onValueChange={(v) => updateSpec("service_location", v)}><SelectTrigger data-testid="field-rental-service"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sans_chauffeur">Sans chauffeur</SelectItem><SelectItem value="avec_chauffeur">Avec chauffeur</SelectItem><SelectItem value="les_deux">Les deux offres</SelectItem></SelectContent></Select></div><Field label="Caution location (USD)" type="number" v={form.specs.caution_location} onChange={(v) => updateSpec("caution_location", v)}/><Field label="Kilométrage inclus / jour" type="number" v={form.specs.kilometrage_inclus} onChange={(v) => updateSpec("kilometrage_inclus", v)}/></>}
              {isRent && <><Field label="Frais dépassement (USD/km)" type="number" v={form.specs.frais_depassement} onChange={(v) => updateSpec("frais_depassement", v)}/><Field label="Zone de circulation" v={form.specs.zone_circulation} onChange={(v) => updateSpec("zone_circulation", v)}/><Field label="Assurance incluse" v={form.specs.assurance} onChange={(v) => updateSpec("assurance", v)}/><Field label="Conditions de location" v={form.specs.conditions_location} onChange={(v) => updateSpec("conditions_location", v)}/><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.specs.livraison_vehicule} onChange={(e) => updateSpec("livraison_vehicule", e.target.checked)} /><span>Livraison du véhicule possible</span></label></>}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl text-brand-navy">Options & équipements</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {OPTIONS_BASE.map((opt) => {
                const on = form.equipment.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleOpt(opt)}
                    className={`text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                      on ? "border-brand-orange bg-brand-orange/10 text-brand-navy font-semibold" : "border-slate-200 hover:border-brand-orange/50"
                    }`}
                    data-testid={`opt-${opt}`}
                  >
                    {on ? "✓ " : ""}
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Où est le véhicule ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Pays</Label><Input value={form.country} onChange={(e) => update("country", e.target.value)} /></div>
              <div><Label>Ville *</Label><Input value={form.city} onChange={(e) => update("city", e.target.value)} required data-testid="field-city" /></div>
              <div><Label>Zone</Label><Input value={form.zone} onChange={(e) => update("zone", e.target.value)} placeholder="Ex : Centre-ville" /></div>
              <div className="md:col-span-3">
                <Label>Emplacement précis (interne)</Label>
                <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Parc, garage, dépôt…" />
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-5">
            <h2 className="font-display text-xl text-brand-navy">Description & prix</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder={`Ex : ${form.specs.marque || "Toyota"} ${form.specs.modele || "Land Cruiser"} — ${form.specs.annee || "2022"}`}
                  required
                  data-testid="field-title"
                />
              </div>
              <div><Label>Title (EN)</Label><Input value={form.title_en} onChange={(e) => update("title_en", e.target.value)} /></div>
              <div><Label>Description (FR)</Label><Textarea rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} /></div>
              <div><Label>Description (EN)</Label><Textarea rows={4} value={form.description_en} onChange={(e) => update("description_en", e.target.value)} /></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>Prix *</Label><Input type="number" value={form.price} onChange={(e) => update("price", e.target.value)} required data-testid="field-price" /></div>
                <div>
                  <Label>Devise</Label>
                  <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="CDF">CDF</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isRent && (
                  <div>
                    <Label>Périodicité</Label>
                    <Select value={form.price_period || "day"} onValueChange={(v) => update("price_period", v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">/ jour</SelectItem>
                        <SelectItem value="week">/ semaine</SelectItem>
                        <SelectItem value="month">/ mois</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <label className="flex items-center gap-2 self-end pb-2 text-sm">
                  <input type="checkbox" checked={form.negotiable} onChange={(e) => update("negotiable", e.target.checked)} />
                  Prix négociable
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Photos & vidéos</h2>
            <MediaUploader value={form.images} onChange={(v) => update("images", v)} accept="image" label="Photos (la 1ère devient l'image de couverture)" maxItems={30} />
            <MediaUploader value={form.videos} onChange={(v) => update("videos", v)} accept="video" label="Vidéos (tour extérieur, moteur, intérieur)" maxItems={5} />
          </div>
        )}

        {step === 7 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Documents</h2>
            <MediaUploader value={form.documents} onChange={(v) => update("documents", v)} accept="document" label="Documents publics (fiche technique, contrat type…)" maxItems={10} />
            <div className="rounded-xl border border-red-100 bg-red-50/40 p-4">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                <FileLock className="h-4 w-4" /> Documents privés — Espace Owner uniquement
              </div>
              <p className="mt-1 text-xs text-red-700/80">
                Carte grise, certificat de dédouanement, pièce du propriétaire… Ces fichiers ne sont visibles qu&apos;après authentification.
              </p>
              <div className="mt-3">
                <MediaUploader
                  value={form.private_documents}
                  onChange={(v) => update("private_documents", v)}
                  accept="document"
                  isPrivate
                  label="Carte grise, certificat de dédouanement, etc."
                  maxItems={20}
                />
              </div>
            </div>
          </div>
        )}

        {step === 8 && (
          <div className="space-y-6">
            <h2 className="font-display text-xl text-brand-navy">Publication</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label>Statut de publication</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => update("status", s.value)}
                        className={`rounded-lg border px-3 py-2 text-sm ${
                          form.status === s.value ? "border-brand-orange bg-brand-orange text-white font-semibold" : "border-slate-200 hover:border-brand-orange/50"
                        }`}
                        data-testid={`status-${s.value}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-3 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)} data-testid="field-featured" />
                  <Star className="h-4 w-4 text-brand-orange" />
                  <span className="text-sm">Mettre en vedette</span>
                </label>
              </div>

              <aside className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Aperçu</div>
                <div className="mt-3 rounded-lg overflow-hidden bg-white border border-slate-200">
                  {form.images[0] ? (
                    <img src={resolveMediaUrl(form.images[0])} alt="" className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-slate-200 flex items-center justify-center text-slate-500 text-xs">Pas de photo</div>
                  )}
                  <div className="p-3 space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">
                      {form.purpose === "sale" ? "À vendre" : "À louer"} · {form.category}
                    </div>
                    <div className="text-sm font-bold text-brand-navy truncate">
                      {form.title || `${form.specs.marque} ${form.specs.modele} ${form.specs.annee}`.trim() || "Titre de l'annonce"}
                    </div>
                    <div className="text-xs text-slate-500">{form.zone && `${form.zone} · `}{form.city}</div>
                    <div className="text-sm font-bold text-brand-navy pt-1">
                      {form.price ? `${Number(form.price).toLocaleString()} ${form.currency}` : "—"}
                      {isRent && form.price_period ? ` /${form.price_period}` : ""}
                    </div>
                    <div className="text-[11px] text-slate-500 pt-1">
                      {[form.specs.km && `${form.specs.km} km`, form.specs.carburant, form.specs.transmission].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[11px] text-slate-500">
                  {form.images.length} photo(s) · {form.videos.length} vidéo(s) · {form.documents.length} doc public(s) · {form.private_documents.length} doc privé(s)
                </div>
              </aside>
            </div>
          </div>
        )}
      </div>

      {step === STEPS.length - 1 && (<SpecializedListingFields sector={form.sector} purpose={form.purpose} category={form.category} price={form.price} specs={form.specs} privateData={form.private_data || {}} publicationStage={form.publication_stage || "published"} onSpecChange={updateSpec} onPrivateChange={(key, value) => setForm((current) => ({ ...current, private_data: { ...(current.private_data || {}), [key]: value } }))} onPublicationStage={(value) => update("publication_stage", value)} />)}


      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wizard-prev">
          <ArrowLeft className="h-4 w-4 mr-1" /> Précédent
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            type="button"
            onClick={() => setStep((s) => s + 1)}
            disabled={!canProceed}
            className="bg-brand-navy hover:bg-brand-navy/90 text-white"
            data-testid="wizard-next"
          >
            Suivant <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={submit}
            disabled={saving || !form.title.trim() || Number(form.price) <= 0}
            className="bg-brand-orange hover:bg-brand-orangeDark text-white"
            data-testid="wizard-submit"
          >
            <Check className="h-4 w-4 mr-1" /> {editing ? "Enregistrer" : "Publier l'annonce"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", tid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
    </div>
  );
}
